import React, { FC } from 'react';
import { create } from 'zustand';

export type CarouselVariants =
  'horizontal' | 'vertical' | 'stack' | 'dynamic' | 'grid';

interface SequenceCarouselState {
  index: number;
  total: number;
  variant: CarouselVariants;
}

export interface CarouselStore {
  sequences: Record<string, SequenceCarouselState>;
  getIndex: (sequenceId: string) => number;
  getTotal: (sequenceId: string) => number;
  getVariant: (sequenceId: string) => CarouselVariants;
  setIndex: (sequenceId: string, index: number) => void;
  setTotal: (sequenceId: string, total: number) => void;
  setVariant: (sequenceId: string, variant: CarouselVariants) => void;
  next: (sequenceId: string) => void;
  prev: (sequenceId: string) => void;
  clearSequence: (sequenceId: string) => void;
}

const getOrCreateSequenceState = (
  sequences: Record<string, SequenceCarouselState>,
  sequenceId: string
): SequenceCarouselState => {
  if (!sequences[sequenceId]) {
    return { index: 0, total: 0, variant: 'horizontal' };
  }
  return sequences[sequenceId];
};

export const useCarouselStore = create<CarouselStore>((set, get) => ({
  sequences: {},
  getIndex: (sequenceId: string) => {
    return getOrCreateSequenceState(get().sequences, sequenceId).index;
  },
  getTotal: (sequenceId: string) => {
    return getOrCreateSequenceState(get().sequences, sequenceId).total;
  },
  getVariant: (sequenceId: string) => {
    return getOrCreateSequenceState(get().sequences, sequenceId).variant;
  },
  setIndex: (sequenceId: string, index: number) => {
    set(state => ({
      sequences: {
        ...state.sequences,
        [sequenceId]: {
          ...getOrCreateSequenceState(state.sequences, sequenceId),
          index
        }
      }
    }));
  },
  setTotal: (sequenceId: string, total: number) => {
    const current = getOrCreateSequenceState(get().sequences, sequenceId);
    if (total === current.total) return;
    set(state => ({
      sequences: {
        ...state.sequences,
        [sequenceId]: {
          ...getOrCreateSequenceState(state.sequences, sequenceId),
          total
        }
      }
    }));
  },
  setVariant: (sequenceId: string, variant: CarouselVariants) => {
    const current = getOrCreateSequenceState(get().sequences, sequenceId);
    if (variant === current.variant) return;
    set(state => ({
      sequences: {
        ...state.sequences,
        [sequenceId]: {
          ...getOrCreateSequenceState(state.sequences, sequenceId),
          variant
        }
      }
    }));
  },
  next: (sequenceId: string) => {
    const state = getOrCreateSequenceState(get().sequences, sequenceId);
    const newIndex = Math.min(state.total - 1, state.index + 1);
    get().setIndex(sequenceId, newIndex);
  },
  prev: (sequenceId: string) => {
    const state = getOrCreateSequenceState(get().sequences, sequenceId);
    const newIndex = Math.max(0, state.index - 1);
    get().setIndex(sequenceId, newIndex);
  },
  clearSequence: (sequenceId: string) => {
    set(state => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [sequenceId]: _, ...rest } = state.sequences;
      return { sequences: rest };
    });
  }
}));

export const useCarouselStoreForSequence = (sequenceId: string) => {
  const store = useCarouselStore();

  return {
    index: store.getIndex(sequenceId),
    total: store.getTotal(sequenceId),
    variant: store.getVariant(sequenceId),
    setIndex: (index: number) => store.setIndex(sequenceId, index),
    setTotal: (total: number) => store.setTotal(sequenceId, total),
    setVariant: (variant: CarouselVariants) =>
      store.setVariant(sequenceId, variant),
    next: () => store.next(sequenceId),
    prev: () => store.prev(sequenceId),
    clearSequence: () => store.clearSequence(sequenceId)
  };
};

export const CarouselProvider: FC = ({ children }) => {
  return <>{children}</>;
};
